import React, { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useTripCartOptionsContext } from 'src/trip-cart-deprecated/hooks/useTripCartOptionsContext';
import SkeletonBlock from 'src/common/components/SkeletonLoader/SkeletonBlock';
import Typography from 'src/common/components/Typography';
import getClassNames from 'src/common/utils/getClassNames';
import styles from './PriceInfo.module.scss';

export type Props = {
  textAlign?: 'left' | 'right';
};

const placeholder = (value: ReactNode) => value || ' ';
const PriceInfo: FC<Props> = ({ textAlign = 'left' }) => {
  const [t] = useTranslation('listing-page');
  const {
    prices,
    priceLoading,
    getFormattedEstimatedRate,
    options: {
      isCostConfirmed,
    },
  } = useTripCartOptionsContext();

  const { cost, costText } = useMemo(() => {
    const showEstimatedRate = prices?.rate != null;
    const formattedRate = showEstimatedRate ? getFormattedEstimatedRate(prices?.rate || '') : '';
    const estimatedRateText = showEstimatedRate ? t('trip-cart--estimated-rate--text') : t('trip-cart--estimated-cost');

    const estimatedCost = prices?.price_sections?.[0]?.formatted_amount || '';

    return {
      cost: showEstimatedRate ? formattedRate : estimatedCost,
      costText: isCostConfirmed ? t('trip-cart--confirmed-cost') : estimatedRateText,
    };
  }, [
    getFormattedEstimatedRate,
    prices?.price_sections,
    prices?.rate,
    isCostConfirmed,
    t,
  ]);
  return (
    <div className={getClassNames(styles, 'root', textAlign)}>
      <SkeletonBlock loading={priceLoading}>
        <Typography
          type="span"
          size="body-l"
          weight="semibold"
        >
          {placeholder(cost)}
        </Typography>
      </SkeletonBlock>
      <SkeletonBlock loading={priceLoading}>
        <Typography
          type="span"
          size="body-s"
          sizeFromBreakpointL="body-m"
          className={styles.costText}
        >
          {placeholder(costText)}
        </Typography>
      </SkeletonBlock>
    </div>
  );
};

export default PriceInfo;
